import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import React, {useEffect, useRef, useState} from "react";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import {subMonths} from "date-fns";
import Filters from "./components/filters.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import slaCamerasService from "../../../services/reports/sla-cameras-service.js";
import reportUtils from "../../../utils/report-utils.js";
import Content from "./components/content.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const dateRef = new Date();
const initialFilters = {
  date_start: subMonths(dateRef, 2),
  date_end: dateRef,
  pcl: [],
  searchDetails: false
}

const filterLabels = {
  date_start: "Data inicial *",
  date_end: "Data final *",
  pcl: "Pontos de coleta",
  searchDetails: "Exibir detalhes",
};

const title = "SLA de câmeras"
const SlaCameras = () => {
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [exportable, setExportable] = useState(false);
  const [reportData, setReportData] = useState(null)

  const [tableColumns, setTableColumns] = useState(() => ({
    id: {label: "ID", visible: true},
    camera: {label: "Câmera", visible: true},
    position: {label: "Posição", visible: true},
    sla: {label: "SLA", visible: true},
    inactive: {label: "Inoperante", visible: true},
    events: {label: "Quantidade de eventos", visible: true}
  }));

  const [headerSelected, setHeaderSelected] = useState(-1);
  const [filters, setFilters] = useState(initialFilters);
  const [fixedTop, setFixedTop] = useState(null);
  const [detailed, setDetailed] = useState(false);
  const buttonRef = useRef(null);
  const printComponentRef = useRef(null)

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        date_start: filters.date_start ? ReportUtils.formatDateOnly(filters.date_start) : "",
        date_end: filters.date_end ? ReportUtils.formatDateOnly(filters.date_end) : ""
      }, filterLabels)
    );

  }, [reportData]);

  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null)
      }
    });

    try {
      const periodValidation = reportUtils.validateOnlyDates(filters.date_start, filters.date_end);

      if (!periodValidation.ok) {
        ReportUtils.alertErrorMessage(periodValidation.message);
        setLoading(false);
        return;
      }

      const formattedFilters = {
        date_start: filters.date_start ? ReportUtils.formatDateOnly(filters.date_start) : null,
        date_end: filters.date_end ? ReportUtils.formatDateOnly(filters.date_end) : null,
        searchDetails: filters.searchDetails ? "Sim" : "Não",
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          date_start: formattedFilters.date_start,
          date_end: formattedFilters.date_end,
          searchDetails: formattedFilters.searchDetails},
        filterLabels, true);

      const { data: response } = await slaCamerasService.apply({
        ...buildFilterObj(),
        filters: auditFilters
      })

      setDetailed(filters.searchDetails);
      setReportData(response.deviceData);

      setExportable(!filters.searchDetails && response.deviceData?.length > 0);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {
      console.error(error)
      ReportUtils.alertErrorMessage(error.response.data.message);

    } finally {
      setLoading(false);
    }
  }
  const clear = () => {
    setFilters(initialFilters);
    setReportData(null);
  }

  function buildFilterObj () {
    const filterObj = {}

    filterObj['date_start'] =  reportUtils.formatDateOnly(filters.date_start);
    filterObj['date_end'] =  reportUtils.formatDateOnly(filters.date_end);
    filterObj['pcl'] =  filters.pcl;
    if(filters.searchDetails){
      filterObj['searchDetails'] = 'on'
    }

    return filterObj;
  }

  useGlobalKey("Enter", apply);

  return(
    <SubScroll dependency={[collapsed, reportData]}>
      <div className="flex">
        <Template isLoading={loading}>
          <Template.Header>
            <Template.Title>{title}</Template.Title>
            <HeaderButtons
              exportable={exportable}
              exportInSheets={true}
              printable={true}
              headerChange={true}
              visibilityColumns={true}
              columns={tableColumns}
              setColumns={setTableColumns}
              printComponentRef={printComponentRef}
              reportData={reportData}
              data={reportData?.length}
              headerSelected={headerSelected}
              setHeaderSelected={setHeaderSelected}
            />
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clear}>
              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>
            </Menu>
          </Template.Body>
        </Template>
        <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} setLoading={setLoading} printComponentRef={printComponentRef} detailed={detailed} tableColumns={tableColumns}/>
            </div>
          </Card.Body>
        </Card>
      </div>
    </SubScroll>
  )
}

export default SlaCameras;